import { ButtonComponent, DropdownMenu, IconButton } from '@/components/commons'
import { ModalPage } from '@/components/ui/modals/ModalPage.component'
import { IUser } from '@/interfaces/user'
import { requestConnection, unblockConnection } from '@/services/connections'
import { environment } from '@/shared/consts/environment'
import { useMutation } from '@/shared/hooks/useMutation'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { UserContext } from '../../context'
import { recommendationCardButton } from '../recommendationCard/RecommendationCard.css'

import { BlockConnectionModal } from '@/components/blockConnectionModal/BlockConnectionModal'
import { useConfirmationModal } from '@/components/confirmationModal/ConfirmationModal'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { Avatar } from 'containers/recommendations/Avatar'
import { TextArea } from '../commons/forms/TextArea.component'
import { Title } from '../commons/typography/Title'
import { validateConnectionExistsById } from './validateConnectionSentReceived'

type RequestConnectionButtonProps = {
  onSuccess?: (user: IUser) => void
  isInRecommendationCard?: boolean
  profileFromProps?: IUser
  className?: string
}

export const RequestConnectionButton = ({
  onSuccess,
  isInRecommendationCard,
  profileFromProps,
  className,
}: RequestConnectionButtonProps) => {
  const { user, setUser } = React.useContext(UserContext)
  const ownProfile = useIsOwnProfile()
  const { profile: profileFromContext } = useProfile()
  const profile = profileFromProps || profileFromContext
  const router = useRouter()
  const {
    connections = [],
    connectionsSent = [],
    connectionsReceived = [],
    blockedUsers = [],
  } = user
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [message, setMessage] = useState('')

  const hideForOwnProfile = ownProfile && !isInRecommendationCard

  useEffect(() => {
    if (router?.query?.requestConnection) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { requestConnection: _, ...rest } = router.query
      router.replace({ query: rest }, undefined, { shallow: true })
      setIsModalVisible(true)
    }
  }, [router])

  const onCloseModal = useCallback(() => {
    setIsModalVisible(false)
    setMessage('')
  }, [])

  const [mutate, { isLoading }] = useMutation({
    mutateMethod: requestConnection,
  })

  const onSubmit = useCallback(async () => {
    // TODO joxxe: to many enpoints are usiing different types and now validations using 'in' are not working
    // this change is not the final solution, but it will fix the issue for now
    const res = (await mutate(profile?.id, message)) as IUser

    if (!res.id) return

    setUser(res)

    if (onSuccess) onSuccess(res)

    onCloseModal()
  }, [onSuccess, profile, message, mutate, setUser, onCloseModal])

  const isIdInConnectionsReceived = useMemo(() => {
    return validateConnectionExistsById({
      id: profile?.id,
      connections: connectionsReceived,
    })
  }, [connectionsReceived, profile])

  const isIdInConnectionsSent = useMemo(() => {
    return validateConnectionExistsById({
      id: profile?.id,
      connections: connectionsSent,
    })
  }, [connectionsSent, profile])

  const isVisible = useMemo(() => {
    const profileBlockedUsers = profile?.blockedUsers || []
    return (
      !isIdInConnectionsReceived &&
      !connections.includes(profile?.id) &&
      !isIdInConnectionsSent &&
      !profileBlockedUsers.includes(user.id)
    )
  }, [
    user,
    connections,
    profile,
    isIdInConnectionsReceived,
    isIdInConnectionsSent,
  ])

  const [unblock] = useMutation({
    mutateMethod: unblockConnection,
  })
  const isRequestSent = useMemo(() => {
    return isIdInConnectionsSent && !connections.includes(profile?.id)
  }, [connections, profile, isIdInConnectionsSent])

  const hasBlockedProfile = useMemo(() => {
    return blockedUsers.includes(profile?.id)
  }, [blockedUsers, profile])
  const showConfirmation = useConfirmationModal({
    title: 'Unblock Connection',
    message: 'Are you sure that you want to unblock this connection?',
  })
  const onClickUnblock = useCallback(async () => {
    const confirmation = await showConfirmation()
    if (!confirmation) return
    const res = await unblock(profile?.id)
    if ('error' in res) return
    if (res?.id) {
      setUser(res)
    }
  }, [profile, setUser, unblock, showConfirmation])
  const connectAction = () => {
    if (!user?.id) {
      router.push(
        `/login?urlCallback=/${profile?.username.toLocaleLowerCase()}?requestConnection=true`
      )
      return
    }
    setIsModalVisible(true)
  }

  if (!ownProfile && isRequestSent)
    return (
      <ButtonComponent
        label="Requested"
        size="xSmall"
        fullWidth={!isInRecommendationCard}
        className={
          isInRecommendationCard && `${recommendationCardButton} ${className}`
        }
        disabled
        onClick={connectAction}
      />
    )

  if (hideForOwnProfile || !isVisible) return null

  if (hasBlockedProfile) {
    return (
      <div
        className="flex flex-row gap-10 items-center justify-stretch"
        style={{ width: '100%' }}
      >
        <ButtonComponent
          className={className}
          label="Blocked"
          size="xSmall"
          fullWidth
          disabled
        />
        {environment.env === 'development' || environment.env === 'staging' ? (
          <DropdownMenu>
            <DropdownMenu.Trigger>
              <IconButton
                style={{ transform: 'rotate(90deg)' }}
                icon="ThreeDotsIcon"
              />
            </DropdownMenu.Trigger>
            <BlockConnectionModal blockedUserId={profile?.id}>
              <DropdownMenu.Content
                align="end"
                sideOffset={20}
                alignOffset={-10}
              >
                <DropdownMenu.Item onClick={onClickUnblock}>
                  <span>Unblock</span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </BlockConnectionModal>
          </DropdownMenu>
        ) : null}
      </div>
    )
  }
  return (
    <>
      <ModalPage show={isModalVisible} onClose={onCloseModal}>
        <div className="flex flex-column items-center gap-16">
          <Avatar
            image={profile?.avatar}
            alt={`${profile?.username}_connection_avatar`}
            size={120}
          />
          <Title
            sizeClass="text-18"
            weightClass="font-bold"
            className="text-center"
            lineHeightClass="leading-28"
          >
            {`Customize your connection`}
            <br />
            {`request to ${profile?.firstName || 'this user'}`}
          </Title>
          <p className="text-16 text-center font-regular leading-28 m-0 text-secondary">
            {'You can add a message to personalize'}
            <br />
            {'your connection request.'}
          </p>
          <TextArea
            usePlaceholder
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ex: Hi! I wanted to connect with you about this great project idea that we could collaborate on..."
          />
          <div className="flex items-center justify-flex-end gap-12 pt-24">
            <ButtonComponent
              variation="secondary"
              size="small"
              onClick={onCloseModal}
              loading={isLoading}
              label="Cancel"
            />
            <ButtonComponent
              size="small"
              onClick={onSubmit}
              loading={isLoading}
              label="Connect"
            />
          </div>
        </div>
      </ModalPage>
      <ButtonComponent
        label="Connect"
        size="xSmall"
        fullWidth={!isInRecommendationCard}
        className={
          isInRecommendationCard && `${recommendationCardButton} ${className}`
        }
        onClick={connectAction}
      />
    </>
  )
}
